// optionTableMixin.js
//
// 선택형옵션(opt) / 추가상품(add) 테이블 공용 - nullHDCount(0/1/2)에 따른 컬럼 인덱스 계산
//
// 배경: 네이버 엑셀 원본의 옵션구분 컬럼 개수가 1~3개로 가변적이라,
//       구분 컬럼이 적을수록 옵션가/재고수량/관리코드/사용여부 컬럼이 앞으로 당겨진다.
//       (예: nullHDCount=0 → 옵션가는 index 3 / nullHDCount=2 → 옵션가는 index 1)
//
// 주의: 이 mixin은 "옵션가/재고수량/관리코드/사용여부"의 위치 계산만 담당한다.
//       헤더 라벨 자체의 구성(getProdut_prc_info 내 라벨 재사용 로직)은 별도 확인 후 다음 단계에서 다룬다.

export default {
    methods: {
      /**
       * nullHDCount(0/1/2)에 따른 옵션가/재고수량/관리코드/사용여부 컬럼 인덱스 반환
       * 기존 4곳(getProdut_prc_info 데이터매핑, upd_checkedOpt, updateOptionValue, priceInsert_new)의
       * if(nHD==0)/else if(nHD==1)/else if(nHD==2) 삼중분기를 대체한다.
       *
       * swapStatusMaCode: 네이버 원본 엑셀의 꼬리 4컬럼 순서가 opt는 [가격,재고,관리코드,사용여부]이고
       * add는 [가격,재고,사용여부,관리코드]로 서로 다르다 (2026-07 실제 엑셀로 확인됨).
       * opt 호출 시 false(기본값), add 호출 시 true로 넘겨야 실제 엑셀 순서와 일치한다.
       */
      getOptColumnIndexes(nullHDCount, swapStatusMaCode = false) {
        const nHD = Number(nullHDCount) || 0;
        const priceIdx = 3 - nHD; // nHD=0→3, nHD=1→2, nHD=2→1
        const qtyIdx = priceIdx + 1;
        if (swapStatusMaCode) {
          return { price: priceIdx, qty: qtyIdx, status: qtyIdx + 1, maCode: qtyIdx + 2 };
        }
        return { price: priceIdx, qty: qtyIdx, maCode: qtyIdx + 1, status: qtyIdx + 2 };
      },
  
      /**
       * nullHDCount에 따라 실제 존재하는 구분값(name1/name2/name3)만 채워서 반환.
       * 없는 슬롯은 빈 문자열("")로 채운다 (upd_checkedOpt의 add1/add2/add3 추출 로직과 동일 규칙).
       */
      getOptNameValues(row, headers, nullHDCount) {
        const nHD = Number(nullHDCount) || 0;
        return {
          name1: row[headers[0]],
          name2: nHD < 2 ? row[headers[1]] : '',
          name3: nHD < 1 ? row[headers[2]] : '',
        };
      },
  
      /**
       * 화면표시용 헤더 라벨 배열 생성.
       * (주의) 기존 화면 표시 관행을 그대로 보존한다: nullHDCount>=1일 때 구분명 라벨 슬롯이
       * 실제로는 가격 데이터를 담는 자리로 재사용되어, 라벨 텍스트와 실제 데이터가 일치하지 않는
       * 케이스가 있다 (2026-07 리팩토링 시점 기준 - 화면 급변경 리스크 회피를 위해 의도적으로 보존, 추후 재검토 예정).
       * swapStatusMaCode: add(추가상품)는 실제 엑셀 순서가 [...,사용여부,관리코드]라 true로 호출.
       */
      buildOptHeaderLabels(names, nullHDCount, priceLabel, swapStatusMaCode = false) {
        const nHD = Number(nullHDCount) || 0;
        const tail3 = swapStatusMaCode ? ['재고수량', '사용여부', '관리코드'] : ['재고수량', '관리코드', '사용여부'];
        if (nHD === 0) {
          return [names.name1, names.name2, names.name3, priceLabel, ...tail3];
        }
        if (nHD === 1) {
          return [names.name1, names.name2, names.name3, ...tail3];
        }
        if (nHD === 2) {
          return [names.name1, names.name2, ...tail3];
        }
        return null; // nullHDCount가 0/1/2가 아닌 잘못된 값
      },
  
      /**
       * DB저장용 헤더 라벨 배열 생성. nullHDCount와 무관하게 항상 고정 7컬럼 구조.
       * swapStatusMaCode: add(추가상품)는 실제 엑셀 순서가 [...,사용여부,관리코드]라 true로 호출.
       * (이걸 true로 맞춰야 엑셀업로드 경로(onFileSelected_add, 원본 엑셀 순서 그대로 보존)와
       *  재로드 경로(getProdut_prc_info)가 동일한 컬럼 순서를 갖게 되어, priceInsert_new가
       *  고정 인덱스로 읽어도 두 경로 모두에서 일관되게 정확한 값을 가져온다.)
       */
      buildFullHeaderLabels(names, priceLabel, swapStatusMaCode = false) {
        const tail3 = swapStatusMaCode ? ['재고수량', '사용여부', '관리코드'] : ['재고수량', '관리코드', '사용여부'];
        return [names.name1, names.name2, names.name3, priceLabel, ...tail3];
      },
  
      /**
       * DB item → headers 배열에 맞춘 row 객체 생성 (화면용/DB저장용 겸용).
       * nullHDCount=0으로 호출하면 DB저장용(고정 7컬럼, headers는 buildFullHeaderLabels 결과)에 맞는 row가 만들어진다.
       * (버그수정) 기존엔 DB저장용 row를 만들 때도 화면용(짧아진) 헤더 배열을 그대로 재사용해서
       * nullHDCount>=1인 경우 배열 범위를 벗어난 인덱스가 undefined 키로 저장되는 문제가 있었음.
       * 이 함수를 buildFullHeaderLabels()가 만든 항상-7컬럼 헤더와 nullHDCount=0 인자로 호출하면
       * 항상 유효한 키로 매핑되어 그 문제가 재발하지 않는다.
       * swapStatusMaCode: headers를 만들 때 사용한 것과 반드시 동일한 값으로 넘겨야 라벨-값이 어긋나지 않는다.
       */
      buildOptRowObject(item, headers, nullHDCount, srcFields, extraFields = {}, swapStatusMaCode = false) {
        const nHD = Number(nullHDCount) || 0;
        const colIdx = this.getOptColumnIndexes(nHD, swapStatusMaCode);
        const row = { ...extraFields };
        row[headers[0]] = item[srcFields.name1];
        if (nHD < 2) row[headers[1]] = item[srcFields.name2];
        if (nHD < 1) row[headers[2]] = item[srcFields.name3];
        row[headers[colIdx.price]] = item[srcFields.price];
        row[headers[colIdx.qty]] = item[srcFields.qty];
        row[headers[colIdx.maCode]] = item[srcFields.maCode];
        row[headers[colIdx.status]] = item[srcFields.status];
        return row;
      },
    },
  };
  